<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import { ref } from 'vue';

const copiedId = ref<string | null>(null);

const copy = async (text: string, id: string) => {
  try {
    await navigator.clipboard.writeText(text);
    copiedId.value = id;
    setTimeout(() => {
      if (copiedId.value === id) copiedId.value = null;
    }, 1500);
  } catch {
    // Presse-papier indisponible (contexte non sécurisé, permission refusée...) :
    // rien de grave, l'utilisateur peut toujours sélectionner le texte à la main.
  }
};

const buttonSnippet = `<a href="https://dartshub.fr/login?client_id=VOTRE_CLIENT_ID&redirect=https://votresite.fr/auth/dartshub/callback"
   style="display:inline-flex;align-items:center;gap:10px;padding:10px 20px;
          background:linear-gradient(135deg,#1e3a5f,#162d4a);color:#fff;
          border-radius:12px;font-family:'Poppins',sans-serif;font-weight:600;
          font-size:0.9rem;text-decoration:none;">
  <img src="https://dartshub.fr/logo-icon.png" alt="" style="height:20px;width:auto;" />
  Se connecter avec DartsHub
</a>`;

const buttonSnippetLight = `<a href="https://dartshub.fr/login?client_id=VOTRE_CLIENT_ID&redirect=https://votresite.fr/auth/dartshub/callback"
   style="display:inline-flex;align-items:center;gap:10px;padding:9px 20px;
          background:#ffffff;color:#1e3a5f;border:2px solid #1e3a5f;
          border-radius:12px;font-family:'Poppins',sans-serif;font-weight:600;
          font-size:0.9rem;text-decoration:none;">
  <img src="https://dartshub.fr/logo-icon-bleu.png" alt="" style="height:20px;width:auto;" />
  Se connecter avec DartsHub
</a>`;

const buttonSnippetGold = `<a href="https://dartshub.fr/login?client_id=VOTRE_CLIENT_ID&redirect=https://votresite.fr/auth/dartshub/callback"
   style="display:inline-flex;align-items:center;gap:10px;padding:10px 20px;
          background:linear-gradient(135deg,#eab308,#ca8a04);color:#1e3a5f;
          border-radius:12px;font-family:'Poppins',sans-serif;font-weight:700;
          font-size:0.9rem;text-decoration:none;">
  <img src="https://dartshub.fr/logo-icon-bleu.png" alt="" style="height:20px;width:auto;" />
  Se connecter avec DartsHub
</a>`;

const redirectSnippet = `function loginWithDartsHub() {
  const clientId = 'VOTRE_CLIENT_ID';
  const redirectUri = 'https://votresite.fr/auth/dartshub/callback';
  const url = 'https://dartshub.fr/login'
    + '?client_id=' + encodeURIComponent(clientId)
    + '&redirect=' + encodeURIComponent(redirectUri);
  window.location.href = url;
}`;

const callbackSnippet = `// Sur https://votresite.fr/auth/dartshub/callback
const params = new URLSearchParams(window.location.hash.slice(1));
const token = params.get('token');

if (token) {
  // Envoyez le token a votre propre backend : c'est LUI qui doit le verifier
  // aupres de DartsHub, jamais le navigateur (le token ne doit pas etre stocke
  // ou fait confiance tel quel cote client).
  fetch('/auth/dartshub/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  }).then(() => {
    window.location.href = '/';
  });
}`;

const verifySnippet = `import httpx

async def verify_dartshub_token(token: str) -> dict:
    async with httpx.AsyncClient() as client:
        resp = await client.get(
            "https://dartshub.fr/partners/me",
            headers={"Authorization": f"Bearer {token}"},
        )
    if resp.status_code != 200:
        raise ValueError("Token DartsHub invalide ou expire")
    return resp.json()
    # {"email": "...", "user_id": 123, "name": "...",
    #  "firstname": "...", "nickname": "...", "client_id": "..."}
    # Charge a vous de creer/retrouver le compte local correspondant
    # (par email ou par user_id) et d'ouvrir votre propre session.`;

const buttonVariants = [
  {
    id: 'dark',
    label: 'Sombre',
    snippet: buttonSnippet,
    logo: '/logo-icon.png',
    style: { background: 'linear-gradient(135deg, #1e3a5f, #162d4a)', color: '#fff', border: 'none' },
  },
  {
    id: 'light',
    label: 'Clair / contour',
    snippet: buttonSnippetLight,
    logo: '/logo-icon-bleu.png',
    style: { background: '#ffffff', color: '#1e3a5f', border: '2px solid #1e3a5f' },
  },
  {
    id: 'gold',
    label: 'Accent doré',
    snippet: buttonSnippetGold,
    logo: '/logo-icon-bleu.png',
    style: { background: 'linear-gradient(135deg, #eab308, #ca8a04)', color: '#1e3a5f', border: 'none', fontWeight: 700 },
  },
];

const stateSnippet = `<!-- Deconnecte -->
<a href="#" onclick="loginWithDartsHub()">Se connecter avec DartsHub</a>

<!-- Connecte (donnees issues de VOTRE session, pas de DartsHub) -->
<div>
  Connecte en tant que <strong>{{ nickname }}</strong>
  <button onclick="logout()">Se deconnecter</button>
</div>`;
</script>

<template>
  <div class="page-wrapper">
    <div class="doc-page">
      <h2 class="page-title">Intégrer la connexion DartsHub</h2>
      <p class="doc-intro">
        Guide pour un site partenaire qui délègue son inscription/connexion à DartsHub, sans accès
        au reste de la plateforme (licences, clubs, tournois). Le principe est le même qu'un
        « Se connecter avec Google » : DartsHub affiche son propre formulaire, vous ne récupérez
        qu'une identité de base en retour.
      </p>

      <div class="doc-note">
        <strong>Avant de commencer : </strong>
        <RouterLink to="/contact" class="doc-note-link">contactez-nous</RouterLink> pour obtenir un
        <code>client_id</code> et enregistrer l'URL exacte de callback (<code>redirect_uri</code>) de votre site. Une
        connexion n'aboutit que si cette URL correspond exactement à celle enregistrée.
      </div>

      <section class="doc-section">
        <h3>1. Le bouton de connexion</h3>
        <p>Trois variantes prêtes à l'emploi, choisissez celle qui s'intègre le mieux à votre charte :</p>

        <div class="doc-btn-gallery">
          <div v-for="variant in buttonVariants" class="doc-btn-card" :key="variant.id">
            <span class="doc-btn-card-label">{{ variant.label }}</span>
            <a href="#" class="doc-btn-preview" :style="variant.style" @click.prevent>
              <img :src="variant.logo" alt="" class="doc-btn-preview-logo" />
              Se connecter avec DartsHub
            </a>
            <button class="doc-copy-btn" @click="copy(variant.snippet, variant.id)">
              {{ copiedId === variant.id ? 'Copié !' : 'Copier le HTML' }}
            </button>
          </div>
        </div>

        <p>
          Chaque bouton « Copier le HTML » ci-dessus copie le code correspondant. Pensez à remplacer
          <code>VOTRE_CLIENT_ID</code> et l'URL de callback par les vôtres avant d'intégrer.
        </p>
      </section>

      <section class="doc-section">
        <h3>2. Construire l'URL dynamiquement</h3>
        <p>Si vous préférez déclencher la redirection en JS plutôt qu'un lien statique :</p>
        <div class="doc-code-block">
          <div class="doc-code-head">
            <span>JavaScript</span>
            <button class="doc-copy-btn" @click="copy(redirectSnippet, 'redirect')">
              {{ copiedId === 'redirect' ? 'Copié !' : 'Copier' }}
            </button>
          </div>
          <pre>{{ redirectSnippet }}</pre>
        </div>
      </section>

      <section class="doc-section">
        <h3>3. Récupérer le token au retour</h3>
        <p>
          Après connexion, DartsHub redirige vers votre <code>redirect_uri</code> avec un token dans le
          fragment d'URL : <code>https://votresite.fr/auth/dartshub/callback#token=...</code>
        </p>
        <div class="doc-code-block">
          <div class="doc-code-head">
            <span>JavaScript - page de callback</span>
            <button class="doc-copy-btn" @click="copy(callbackSnippet, 'callback')">
              {{ copiedId === 'callback' ? 'Copié !' : 'Copier' }}
            </button>
          </div>
          <pre>{{ callbackSnippet }}</pre>
        </div>
      </section>

      <section class="doc-section">
        <h3>4. Vérifier le token côté serveur</h3>
        <p>
          Votre backend appelle <code>GET /partners/me</code> avec le token en Bearer pour vérifier son
          authenticité et récupérer l'identité, c'est DartsHub qui valide, vous n'avez jamais besoin de
          décoder le token vous-même :
        </p>
        <div class="doc-code-block">
          <div class="doc-code-head">
            <span>Python</span>
            <button class="doc-copy-btn" @click="copy(verifySnippet, 'verify')">
              {{ copiedId === 'verify' ? 'Copié !' : 'Copier' }}
            </button>
          </div>
          <pre>{{ verifySnippet }}</pre>
        </div>
      </section>

      <section class="doc-section">
        <h3>5. Afficher l'état connecté / déconnecté</h3>
        <p>
          À partir de là, c'est votre session à vous (cookie, JWT maison...) qui pilote l'affichage,
          DartsHub ne maintient pas de session sur votre site :
        </p>
        <div class="doc-code-block">
          <div class="doc-code-head">
            <span>HTML</span>
            <button class="doc-copy-btn" @click="copy(stateSnippet, 'state')">
              {{ copiedId === 'state' ? 'Copié !' : 'Copier' }}
            </button>
          </div>
          <pre>{{ stateSnippet }}</pre>
        </div>
      </section>

      <section class="doc-section">
        <h3>À savoir</h3>
        <ul class="doc-list">
          <li>À la <strong>toute première</strong> connexion d'un utilisateur vers votre <code>client_id</code>,
            DartsHub affiche un court écran de consentement (« Ce site va recevoir : nom, prénom, pseudo, email ») avant
            de rediriger, l'utilisateur clique « Autoriser » puis arrive normalement sur votre <code>redirect_uri</code>
            avec le token, exactement comme décrit ci-dessus. Rien à implémenter de votre côté : cet écran est géré
            entièrement par DartsHub et n'apparaît plus aux connexions suivantes du même utilisateur (sauf s'il révoque
            l'autorisation depuis son profil).
          </li>
          <li>Le token retourné n'est valable que <strong>15 minutes</strong>, mais ce n'est pas une durée de session :
            c'est juste la fenêtre pour l'échange initial (votre backend le vérifie via <code>/partners/me</code> dès
            le retour, en quelques secondes). Ensuite c'est <strong>votre propre session</strong> qui prend le relais,
            l'utilisateur ne se reconnecte pas à chaque fois, il reste connecté chez vous comme sur n'importe quel site.
          </li>
          <li>Il ne contient jamais de rôle, licence, club ni aucune autre donnée DartsHub interne, seulement
            <code>email</code>, <code>name</code>, <code>firstname</code>, <code>nickname</code>, <code>user_id</code>.
          </li>
          <li>L'inscription et la connexion via Google sont gérées automatiquement par la même page DartsHub, aucune
            intégration supplémentaire n'est nécessaire de votre côté.</li>
          <li><code>redirect_uri</code> doit correspondre exactement à celle enregistrée pour votre
            <code>client_id</code>, pas de préfixe, pas de wildcard.
          </li>
          <li><code>:</code> et <code>//</code> n'ont pas besoin d'être encodés dans <code>redirect=https://...</code>,
            ce ne sont pas des caractères réservés dans une valeur de paramètre. En revanche, si votre URL de callback
            contient elle-même <code>&amp;</code> ou <code>=</code> (un paramètre de requête), encodez-la avec
            <code>encodeURIComponent()</code> (voir l'exemple JS) pour qu'elle ne casse pas l'URL de connexion.
          </li>
        </ul>
      </section>
    </div>

    <Footer backLink />
  </div>
</template>

<style scoped>
.page-wrapper {
  min-height: 100svh;
  display: flex;
  flex-direction: column;
}

.doc-page {
  flex: 1;
  max-width: 900px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  color: var(--text);
}

.page-title {
  margin-bottom: 1rem;
}

.doc-intro {
  line-height: 1.7;
  color: var(--text-muted);
  margin-bottom: 1.25rem;
}

.doc-note {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--primary) 25%, transparent);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 0.88rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.doc-note-link {
  color: var(--primary);
  font-weight: 600;
  text-decoration: underline;
}

.doc-btn-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 1rem 0 1.5rem;
}

.doc-btn-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 16px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 12px;
  flex: 1 1 220px;
}

.doc-btn-card-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.doc-btn-preview {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: default;
}

.doc-btn-preview-logo {
  height: 20px;
  width: auto;
}

.doc-section {
  margin-top: 2rem;
}

.doc-section h3 {
  margin-bottom: 0.4rem;
}

.doc-section p {
  line-height: 1.7;
  color: var(--text-muted);
}

code {
  background: var(--bg-input);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.86em;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.doc-code-block {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 0.75rem;
}

.doc-code-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: var(--bg-input);
  border-bottom: 1px solid var(--border);
  font-size: 0.76rem;
  color: var(--text-muted);
  font-weight: 600;
  letter-spacing: 0.3px;
}

.doc-copy-btn {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-muted);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.doc-copy-btn:hover {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.doc-code-block pre {
  margin: 0;
  padding: 14px 16px;
  overflow-x: auto;
  font-size: 0.82rem;
  line-height: 1.55;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  background: var(--bg);
  color: var(--text);
}

.doc-list {
  line-height: 1.8;
  color: var(--text-muted);
  padding-left: 1.2rem;
}

</style>
